import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Result } from '@blade/std-lib'
import { Button, DownIcon } from '@blade/ui-kit'
import { PlatformUtil } from '@root/shared/core'
import { Dialog, ShareArrowIcon, useSnackbar } from '@root/shared/ui-kit'

import { PnlCardCanvas } from './pnl-card-canvas'

import type { CanvasPnlBuilder } from '../lib/canvas-pnl-builder'
import type { BestDeal } from '@root/entities/profile'
import type { UserShort } from '@root/entities/user-short'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  bestDeal: BestDeal.BestDeal
  user: UserShort.UserShort
}

export const PnLShareBestDealModal: React.FC<Props> = ({
  open,
  onOpenChange,
  bestDeal,
  user,
}) => {
  const rendererRef = useRef<CanvasPnlBuilder | undefined>(undefined)
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()

  const handleDownload = async () => {
    const renderer = rendererRef.current
    if (!renderer) {
      return
    }

    const dataUrl = renderer.toDataUrl()
    const fileName = `kol.trade Best Deal (${Date.now()}).png`

    let downloadResult = await PlatformUtil.downloadFileFromURL(
      dataUrl,
      fileName,
    )

    if (Result.isFailure(downloadResult)) {
      const blob = await renderer.toBlob()
      if (blob) {
        downloadResult = await PlatformUtil.downloadFile(blob, fileName)
      }
    }

    if (Result.isSuccess(downloadResult)) {
      showSnackbar({
        intent: 'success',
        text: t('profile.best_deal.download_success'),
        delay: 1500,
      })
    } else {
      showSnackbar({
        intent: 'error',
        text: t('profile.best_deal.download_error'),
        delay: 1500,
      })
    }
  }

  const handleShare = async () => {
    const renderer = rendererRef.current
    if (!renderer) {
      return
    }

    const fileName = `kol.trade Best Deal (${Date.now()}).png`
    const blob = await renderer.toBlob()

    if (blob) {
      const shareResult = await PlatformUtil.share({
        files: [new File([blob], fileName, { type: 'image/png' })],
      })
      return Result.match(shareResult, {
        onFailure: () => false,
        onSuccess: () => true,
      })
    }
    return false
  }

  return (
    <Dialog
      variant="functional"
      open={open}
      onOpenChange={onOpenChange}
      describedBy={t('profile.best_deal.title')}
    >
      <Dialog.Header.Functional title={t('profile.best_deal.title')} />
      <Dialog.Content.Functional $variant={0}>
        <Content>
          <PnlCardCanvas
            user={user}
            bestDeal={bestDeal}
            rendererRef={rendererRef}
          />
          <ButtonWrapper>
            <Button
              onClick={handleDownload}
              size="m"
              variant="ghost"
              radius="rounded"
              fullWidth
              iconLeft={<DownIcon />}
            >
              {t('common.download')}
            </Button>
            <Button
              onClick={handleShare}
              size="m"
              color="primary"
              radius="rounded"
              fullWidth
              iconLeft={<ShareArrowIcon />}
            >
              {t('common.share')}
            </Button>
          </ButtonWrapper>
        </Content>
      </Dialog.Content.Functional>
    </Dialog>
  )
}

const Content = styled.div`
  width: 420px;
`
const ButtonWrapper = styled.div`
  padding: 0 16px 24px;
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr 1fr;
`
